/**
 * TanStack Query hooks for /api/v1/approval-rules/.
 *
 * Gated by the same `registration_rules` feature flag as ValidationRule -
 * callers should feature-gate the UI with <FeatureGate flag="registration_rules">
 * before mounting hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import type { PaginatedResponse } from '@/shared/types/api'

export type ApprovalTargetStatus = 'pending_approval'

export interface ApprovalRule {
  uuid: string
  name: string
  description: string | null
  conditions: PredicateNode | null
  severity: Severity
  message: string
  is_active: boolean
  priority: number
  target_status: ApprovalTargetStatus
  required_reason_codes: string[]
  required_reason_code_slugs: string[]
  resolver_roles: string[]
  resolver_role_names: string[]
  created_at: string
  updated_at: string
}

export interface ApprovalRuleInput {
  name: string
  description?: string | null
  conditions: PredicateNode | null
  severity: Severity
  message: string
  is_active?: boolean
  priority?: number
  target_status: ApprovalTargetStatus
  required_reason_codes: string[]
  resolver_roles: string[]
}

const KEY = ['approval-rules'] as const

export function useApprovalRules(options?: { enabled?: boolean }) {
  return useQuery<ApprovalRule[]>({
    queryKey: KEY,
    queryFn: () =>
      api
        .get<PaginatedResponse<ApprovalRule>>('/v1/approval-rules/', {
          params: { page_size: 200 },
        })
        .then((r) => r.data.results),
    staleTime: 30_000,
    enabled: options?.enabled,
  })
}

export function useCreateApprovalRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ApprovalRuleInput) =>
      api.post('/v1/approval-rules/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateApprovalRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...data }: { uuid: string } & Partial<ApprovalRuleInput>) =>
      api.patch(`/v1/approval-rules/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteApprovalRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/v1/approval-rules/${uuid}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export interface ApprovalRuleHit {
  rule_uuid: string
  name: string
  severity: Severity
  message: string
  target_status: string
}

export interface ApprovalEvaluateResult {
  hits: ApprovalRuleHit[]
}

export interface ApprovalEvaluateRequest {
  context: {
    fields?: Record<string, unknown>
    entities?: Record<string, Record<string, unknown>>
    aggregates?: Record<string, unknown>
    roles?: string[]
    permissions?: string[]
  }
}

/** Pre-submission check - call before creating the registration. */
export async function evaluateApprovalRules(
  body: ApprovalEvaluateRequest,
): Promise<ApprovalEvaluateResult> {
  const r = await api.post('/v1/approval-rules/evaluate/', body)
  return r.data as ApprovalEvaluateResult
}
